// RingCentral texting — Slice 1 (send-only from the team line).
// Credentials live in Firestore integrations/ringCentral (same pattern as
// BombBomb) and are only USED by the functions-api backend; the browser
// never talks to RingCentral directly. Every message (sent or failed) is a
// doc in the textMessages collection, linked to a lead/deal by id.
import { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  onSnapshot,
  writeBatch,
} from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { actsAsClientCare } from "./roles";
import { authedFetch } from "./authedFetch";

const API_BASE = "https://us-central1-team-edelstein.cloudfunctions.net/api";
const INTEGRATION_DOC = ["integrations", "ringCentral"];
// Non-secret mirror of the texting config (teamNumber, smsNumbers, connected
// flag). Texting staff read THIS; the secret ringCentral doc above is
// server-only (the browser may write it as admin but never read it back).
const PUBLIC_DOC = ["integrations", "ringCentralPublic"];
const AGENT_APP_DOC = ["integrations", "ringCentralAgent"];
export const RC_OAUTH_REDIRECT = `${API_BASE}/rc-oauth-callback`;

// ---- Pure helpers (unit-tested) ----

// US-centric E.164: 10 digits → +1XXXXXXXXXX; 11 starting with 1 → +digits;
// already +… → kept. Anything else is "" (we refuse to guess).
// Keep in sync with rcNormalizePhone in functions-api/index.js.
export function normalizePhoneE164(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (s.startsWith("+")) {
    const digits = s.slice(1).replace(/\D/g, "");
    return digits.length >= 11 ? `+${digits}` : "";
  }
  const digits = s.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return "";
}

// onSnapshot gives unordered docs (no orderBy in the query — avoids needing
// a composite index). Sort client-side, oldest first.
export function threadFromDocs(docs) {
  return [...docs].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
}

// Who can see/use texting in the UI. Rollout is Team Lead (Admin) + Client
// Care (Supervisor) first; agents (Employee) come later — Henry's call
// 2026-07-17 — EXCEPT agents with the "Client care duties" switch, who work
// the file queues and need the texting surface too (2026-07-31).
export function canUseTexting(user) {
  return user?.role === "Admin" || actsAsClientCare(user);
}

// ---- Firestore (connection state) ----

// Reads the non-secret texting config (teamNumber, smsNumbers, connected flag)
// from integrations/ringCentralPublic. The secret ringCentral doc (clientId/
// clientSecret/jwt) is server-only and never read from the browser.
export async function getRingCentralIntegration() {
  try {
    const snap = await getDoc(doc(firestored, ...PUBLIC_DOC));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.error("getRingCentralIntegration failed:", e);
    return null;
  }
}

export async function saveRingCentralIntegration(
  { clientId, clientSecret, jwt },
  userId,
) {
  const connectedAt = Date.now();
  // Secret creds -> the server-only ringCentral doc. Admins may WRITE it even
  // though nobody may READ it back from the browser.
  await setDoc(
    doc(firestored, ...INTEGRATION_DOC),
    {
      clientId: String(clientId || "").trim(),
      clientSecret: String(clientSecret || "").trim(),
      jwt: String(jwt || "").trim(),
      connectedBy: userId || "",
      connectedAt,
    },
    { merge: true },
  );
  // Non-secret "is it connected?" flag -> the doc texting staff CAN read, so the
  // Settings screen can show connection status without reading the secret.
  await setDoc(
    doc(firestored, ...PUBLIC_DOC),
    { connected: true, connectedBy: userId || "", connectedAt },
    { merge: true },
  );
}

// Choose which SMS-capable number the whole CRM texts from. The backend reads
// teamNumber off the secret doc on every send; the From picker reads it off the
// public doc. Write both so they never drift.
export async function saveRingCentralTeamNumber(teamNumber) {
  const num = String(teamNumber || "").trim();
  await Promise.all([
    setDoc(
      doc(firestored, ...INTEGRATION_DOC),
      { teamNumber: num },
      { merge: true },
    ),
    setDoc(doc(firestored, ...PUBLIC_DOC), { teamNumber: num }, { merge: true }),
  ]);
}

// ---- Per-agent connections ("Connect my RingCentral") ----

// Pure builder for RingCentral's authorize URL (unit-tested).
export function buildRcAuthorizeUrl(clientId, state) {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: RC_OAUTH_REDIRECT,
    state,
  });
  return `https://platform.ringcentral.com/restapi/oauth/authorize?${params.toString()}`;
}

export async function getRcAgentApp() {
  try {
    const snap = await getDoc(doc(firestored, ...AGENT_APP_DOC));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.error("getRcAgentApp failed:", e);
    return null;
  }
}

export async function saveRcAgentApp({ clientId, clientSecret }, userId) {
  await setDoc(
    doc(firestored, ...AGENT_APP_DOC),
    {
      clientId: String(clientId || "").trim(),
      clientSecret: String(clientSecret || "").trim(),
      connectedBy: userId || "",
      connectedAt: Date.now(),
    },
    { merge: true },
  );
}

// Mint a one-time state doc tying the OAuth round-trip to this CRM user,
// and return the RingCentral login URL to open. Returns null when the agent
// connect app hasn't been configured by the Team Lead yet.
export async function startRcConnect(uid) {
  const app = await getRcAgentApp();
  if (!app?.clientId) return null;
  const state = (window.crypto?.randomUUID?.() || `${uid}-${Date.now()}`).replace(/[^a-zA-Z0-9-]/g, "");
  await setDoc(doc(firestored, "rcConnectStates", state), {
    uid,
    createdAt: Date.now(),
  });
  return buildRcAuthorizeUrl(app.clientId, state);
}

export function subscribeMyRcConnection(uid, cb) {
  return onSnapshot(
    doc(firestored, "rcConnections", uid),
    (snap) => cb(snap.exists() ? snap.data() : null),
    (err) => console.error("subscribeMyRcConnection failed:", err),
  );
}

export async function disconnectMyRc(uid) {
  await deleteDoc(doc(firestored, "rcConnections", uid));
}

// ---- Live thread ----

// Subscribe to the messages for one lead OR one deal. Returns the
// unsubscribe function. cb receives the sorted array on every change.
export function subscribeTextThread({ leadId, dealId }, cb) {
  const base = collection(firestored, "textMessages");
  const q = leadId
    ? query(base, where("leadId", "==", leadId))
    : query(base, where("dealId", "==", dealId));
  return onSnapshot(
    q,
    (snap) =>
      cb(threadFromDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
    (err) => console.error("subscribeTextThread failed:", err),
  );
}

// ---- Automated texts: settings + templates ----

// Henry's rule (2026-07-19): NEVER use em dashes in client-facing text copy.
// Texts should read like a human typed them. Short sentences, plain breaks.
export const TEXTING_DEFAULT_TEMPLATES = {
  autoReplyTemplate:
    "Hi {firstName}! Thanks for reaching out to Team Edelstein. We got your inquiry and one of us will call you shortly. Talk soon!",
  portalLinkTemplate:
    "Hi {firstName}, it's Team Edelstein! Here's the link to your forms, it takes just a few minutes: {portalLink}",
  nudgeTemplate:
    "Hi {firstName}, just checking in! Any homes catch your eye this week? Happy to set up showings whenever works for you.",
  milestoneTemplate:
    "Big news, {firstName}! {address} just moved to {stage} 🎉 We'll walk you through every next step. Team Edelstein",
  apptReminderTemplate:
    "Hi {firstName}! Confirming our appointment {apptDate}. See you then!",
  closingReminderTemplate:
    "Hi {firstName}, {address} closes on {closingDate}! We'll walk you through everything before then. Questions? Just reply here.",
  reviewRequestTemplate:
    "Hi {firstName}, congrats again on {address}! If you had a great experience with us, a quick Google review would mean the world: {reviewLink}",
  anniversaryTemplate:
    "Happy one year in your home, {firstName}! Hope {address} has been everything you wanted. We're always here if you need anything.",
};

// Settings live in integrations/textingSettings. EVERY switch defaults OFF.
export async function getTextingSettings() {
  let s = {};
  try {
    const snap = await getDoc(doc(firestored, "integrations", "textingSettings"));
    s = snap.exists() ? snap.data() : {};
  } catch (e) {
    console.error("getTextingSettings failed:", e);
  }
  return {
    autoReplyEnabled: false,
    portalTextEnabled: false,
    nudgeEnabled: false,
    milestoneEnabled: false,
    dripEnabled: false,
    dripSteps: [],
    apptReminderEnabled: false,
    closingReminderEnabled: false,
    reviewRequestEnabled: false,
    anniversaryEnabled: false,
    aiReplyEnabled: false,
    reviewLink: "",
    nudgeDays: 7,
    milestoneStages: "Under Contract, Cleared to Close, Closed",
    quietStartHour: 21,
    quietEndHour: 8,
    ...TEXTING_DEFAULT_TEMPLATES,
    ...s,
  };
}

export async function saveTextingSettings(patch, userId) {
  await setDoc(
    doc(firestored, "integrations", "textingSettings"),
    { ...patch, updatedBy: userId || "", updatedAt: Date.now() },
    { merge: true },
  );
}

// Fill {placeholders} in a template. Unknown tokens are left visible so a
// typo is obvious instead of silently blank. (Pure — unit-tested.)
export function renderTemplate(tpl, vars = {}) {
  return String(tpl || "").replace(/\{(\w+)\}/g, (whole, key) =>
    vars[key] !== undefined && vars[key] !== null && vars[key] !== ""
      ? String(vars[key])
      : whole,
  );
}

// Comma-separated stage config → clean array. (Pure — unit-tested.)
export function parseStageList(csv) {
  return String(csv || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

// First name for templates: "Sarah Mitchell & Bob" → "Sarah".
export function firstNameOf(fullName) {
  return String(fullName || "").trim().split(/[\s&,]+/)[0] || "";
}

// ---- From-number picking (shared by threads + group text) ----

export const fmtPhone = (e164) => {
  const d = String(e164 || "").replace(/\D/g, "");
  return d.length === 11
    ? `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`
    : e164 || "";
};

// The picker remembers each person's last choice (per device).
const prefKey = (uid) => `tesTextFrom:${uid || "anon"}`;
export function getPreferredFrom(uid) {
  try {
    return window.localStorage.getItem(prefKey(uid)) || "";
  } catch (e) {
    return "";
  }
}
export function savePreferredFrom(uid, num) {
  try {
    window.localStorage.setItem(prefKey(uid), num || "");
  } catch (e) {
    // private mode etc.: the default just won't stick
  }
}

// Options for the From picker: my connected number first, then team numbers.
// defaultNumber = the user's remembered pick when still valid, else mine,
// else the team default.
export function useFromNumberOptions(user) {
  const [teamNumbers, setTeamNumbers] = useState([]);
  const [teamDefault, setTeamDefault] = useState("");
  const [conn, setConn] = useState(null);
  useEffect(() => {
    let alive = true;
    getRingCentralIntegration().then((integ) => {
      if (!alive) return;
      setTeamNumbers(Array.isArray(integ?.smsNumbers) ? integ.smsNumbers : []);
      setTeamDefault(integ?.teamNumber || "");
    });
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    if (!user?.id) return undefined;
    return subscribeMyRcConnection(user.id, setConn);
  }, [user?.id]);

  const options = [];
  if (conn?.status === "connected" && conn.ownNumber) {
    options.push({ num: conn.ownNumber, label: `${fmtPhone(conn.ownNumber)} · mine` });
  }
  teamNumbers.forEach((num) => {
    if (!options.some((o) => o.num === num)) {
      options.push({ num, label: `${fmtPhone(num)} · team` });
    }
  });
  const saved = getPreferredFrom(user?.id);
  const defaultNumber =
    saved && options.some((o) => o.num === saved)
      ? saved
      : options[0]?.num || teamDefault || "";
  return { options, defaultNumber };
}

// ---- Automation triggers (called from lead/portal flows; best-effort) ----

export async function contactPhoneOf(contactId) {
  if (!contactId) return "";
  const { getContactsByIds } = await import("./firebasefunctions");
  const { normalizeContact } = await import("./contactNormalize");
  const raw = (await getContactsByIds([contactId]))[0];
  return normalizePhoneE164(normalizeContact(raw)?.phone || "");
}

// Instant auto-reply when a lead is created BY HAND (never from bulk import).
export async function maybeSendNewLeadAutoReply({ leadId, contactId, contactName }) {
  try {
    const s = await getTextingSettings();
    if (!s.autoReplyEnabled) return { skipped: "off" };
    const phone = await contactPhoneOf(contactId);
    if (!phone) return { skipped: "no-phone" };
    const body = renderTemplate(s.autoReplyTemplate, {
      firstName: firstNameOf(contactName),
    });
    if (!body.trim()) return { skipped: "empty-template" };
    return await sendText({
      to: phone,
      body,
      leadId,
      contactName,
      source: "auto-reply",
    });
  } catch (e) {
    console.error("auto-reply failed:", e);
    return { ok: false };
  }
}

// Portal link, texted right after the forms email goes out.
export async function maybeSendPortalLinkText({ leadId, dealId, contactId, contactName, url }) {
  try {
    const s = await getTextingSettings();
    if (!s.portalTextEnabled || !url) return { skipped: "off" };
    const phone = await contactPhoneOf(contactId);
    if (!phone) return { skipped: "no-phone" };
    const body = renderTemplate(s.portalLinkTemplate, {
      firstName: firstNameOf(contactName),
      portalLink: url,
    });
    if (!body.trim()) return { skipped: "empty-template" };
    return await sendText({
      to: phone,
      body,
      leadId,
      dealId,
      contactName,
      source: "portal-link",
    });
  } catch (e) {
    console.error("portal-link text failed:", e);
    return { ok: false };
  }
}

// ---- Unread tracking ----

// One shared Firestore listener for ALL unread inbound texts, fanned out to
// every component that calls useUnreadTexts(). Counts stay tiny (unread
// only), so a single collection listener is cheap.
let unreadCache = { leads: {}, deals: {}, unmatched: 0 };
const unreadSubscribers = new Set();
let unreadUnsub = null;

function ensureUnreadListener() {
  if (unreadUnsub) return;
  const q = query(
    collection(firestored, "textMessages"),
    where("direction", "==", "inbound"),
    where("read", "==", false),
  );
  unreadUnsub = onSnapshot(
    q,
    (snap) => {
      const next = { leads: {}, deals: {}, unmatched: 0 };
      snap.docs.forEach((d) => {
        const m = d.data();
        if (m.dismissed) return;
        if (m.leadId) next.leads[m.leadId] = (next.leads[m.leadId] || 0) + 1;
        else if (m.dealId) next.deals[m.dealId] = (next.deals[m.dealId] || 0) + 1;
        else next.unmatched += 1;
      });
      unreadCache = next;
      unreadSubscribers.forEach((fn) => fn(next));
    },
    (err) => console.error("unread texts listener failed:", err),
  );
}

// { leads: {leadId: count}, deals: {dealId: count}, unmatched: n }
export function useUnreadTexts(enabled = true) {
  const [state, setState] = useState(unreadCache);
  useEffect(() => {
    if (!enabled) return undefined;
    ensureUnreadListener();
    unreadSubscribers.add(setState);
    setState(unreadCache);
    return () => unreadSubscribers.delete(setState);
  }, [enabled]);
  return state;
}

// Mark every unread inbound message on a record as read (thread opened).
export async function markThreadRead({ leadId, dealId }) {
  try {
    const base = collection(firestored, "textMessages");
    const q = leadId
      ? query(base, where("leadId", "==", leadId))
      : query(base, where("dealId", "==", dealId));
    const snap = await new Promise((resolve, reject) => {
      const unsub = onSnapshot(q, (s) => { unsub(); resolve(s); }, reject);
    });
    const unread = snap.docs.filter(
      (d) => d.data().direction === "inbound" && d.data().read === false,
    );
    if (unread.length === 0) return;
    const batch = writeBatch(firestored);
    unread.forEach((d) => batch.update(d.ref, { read: true }));
    await batch.commit();
  } catch (e) {
    console.error("markThreadRead failed:", e);
  }
}

// ---- Unmatched inbound texts (no lead/deal found) ----

export function subscribeUnmatchedTexts(cb) {
  const q = query(
    collection(firestored, "textMessages"),
    where("direction", "==", "inbound"),
    where("leadId", "==", ""),
  );
  return onSnapshot(
    q,
    (snap) =>
      cb(
        threadFromDocs(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((m) => !m.dealId && !m.dismissed),
        ).reverse(), // newest first for the Settings list
      ),
    (err) => console.error("subscribeUnmatchedTexts failed:", err),
  );
}

// Cancel a scheduled (not-yet-sent) message from its thread.
export async function cancelScheduledText(messageId) {
  await setDoc(
    doc(firestored, "textMessages", messageId),
    { status: "cancelled", cancelledAt: Date.now() },
    { merge: true },
  );
}

export async function dismissUnmatchedText(messageId) {
  await setDoc(
    doc(firestored, "textMessages", messageId),
    { dismissed: true, read: true },
    { merge: true },
  );
}

// Bulk "Dismiss All" for the Unmatched list — clears every passed message in
// batched writes. Returns how many were dismissed. (A batch caps at 500 writes,
// so chunk to stay well under.)
export async function dismissUnmatchedTexts(messageIds) {
  const ids = (messageIds || []).filter(Boolean);
  if (!ids.length) return 0;
  for (let i = 0; i < ids.length; i += 450) {
    const batch = writeBatch(firestored);
    ids.slice(i, i + 450).forEach((id) => {
      batch.set(
        doc(firestored, "textMessages", id),
        { dismissed: true, read: true },
        { merge: true },
      );
    });
    await batch.commit();
  }
  return ids.length;
}

// Attach every message from a phone number to a lead or deal — used by the
// Unmatched list's "Link" action. Backfills the whole conversation.
export async function linkTextNumberToRecord(
  fromNumber,
  { leadId = "", dealId = "", contactName = "" },
) {
  const q = query(
    collection(firestored, "textMessages"),
    where("fromNumber", "==", fromNumber),
  );
  const snap = await new Promise((resolve, reject) => {
    const unsub = onSnapshot(q, (s) => { unsub(); resolve(s); }, reject);
  });
  const orphans = snap.docs.filter(
    (d) => !d.data().leadId && !d.data().dealId,
  );
  if (orphans.length === 0) return 0;
  const batch = writeBatch(firestored);
  orphans.forEach((d) => batch.update(d.ref, { leadId, dealId, contactName }));
  await batch.commit();
  return orphans.length;
}

// Thread for a bare number (no lead/deal linked yet): everything sent to or
// received from it. Two listeners merged, since Firestore can't OR-query.
export function subscribeThreadForNumber(number, cb) {
  const base = collection(firestored, "textMessages");
  let fromDocs = [];
  let toDocs = [];
  const emit = () => {
    const byId = {};
    [...fromDocs, ...toDocs].forEach((m) => {
      byId[m.id] = m;
    });
    cb(threadFromDocs(Object.values(byId)));
  };
  const u1 = onSnapshot(
    query(base, where("fromNumber", "==", number)),
    (s) => {
      fromDocs = s.docs.map((d) => ({ id: d.id, ...d.data() }));
      emit();
    },
    (e) => console.error("number thread (from) failed:", e),
  );
  const u2 = onSnapshot(
    query(base, where("toNumber", "==", number)),
    (s) => {
      toDocs = s.docs.map((d) => ({ id: d.id, ...d.data() }));
      emit();
    },
    (e) => console.error("number thread (to) failed:", e),
  );
  return () => {
    u1();
    u2();
  };
}

// ---- Backend calls ----

// Returns { ok, message } or { ok: false, error }. fromNumber is optional —
// omitted, the backend uses the team-line default from Settings. source
// "manual" (default) skips the automated guardrails; anything else gets
// quiet-hours queueing + 5-minute dedupe on the backend. `to` may be an
// array (deal group send); `sendAt` in the future schedules instead of sends.
export async function sendText({ to, body, fromNumber, sendAt, leadId, dealId, contactName, user, source }) {
  try {
    const res = await authedFetch(`${API_BASE}/rc-send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        to,
        body,
        source: source || "manual",
        sendAt: Number(sendAt) || 0,
        fromNumber: fromNumber || "",
        leadId: leadId || "",
        dealId: dealId || "",
        contactName: contactName || "",
        sentByUid: user?.id || "",
        sentByName:
          [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
          user?.email ||
          "",
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const fallback =
        res.status === 404
          ? "Texting isn't live yet — the backend needs its update deployed."
          : `HTTP ${res.status}`;
      return { ok: false, error: data.error || fallback };
    }
    return { ok: true, message: data.message };
  } catch (e) {
    return { ok: false, error: e?.message || "Network error" };
  }
}

// ---- AI reply drafts ----

// AI features are Team Lead only (Henry's call 2026-07-19): Client Care can
// text, but ✨ drafting is Admin-exclusive. NO agents.
export function canUseAi(user) {
  return user?.role === "Admin";
}

// Henry's voice guide: pasted once in the Claude AI tab, included in every
// AI draft prompt so replies sound like him.
export async function saveVoiceProfile(voiceProfile, userId) {
  await setDoc(
    doc(firestored, "integrations", "anthropic"),
    {
      voiceProfile: String(voiceProfile || "").trim(),
      voiceUpdatedBy: userId || "",
      voiceUpdatedAt: Date.now(),
    },
    { merge: true },
  );
}

export async function getAnthropicIntegration() {
  try {
    const snap = await getDoc(doc(firestored, "integrations", "anthropic"));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.error("getAnthropicIntegration failed:", e);
    return null;
  }
}

export async function saveAnthropicKey(apiKey, userId) {
  await setDoc(
    doc(firestored, "integrations", "anthropic"),
    {
      apiKey: String(apiKey || "").trim(),
      connectedBy: userId || "",
      connectedAt: Date.now(),
    },
    { merge: true },
  );
}

// Ask Claude (via the backend) to draft the next reply for a thread.
export async function suggestReply({ leadId, dealId }) {
  try {
    const res = await authedFetch(`${API_BASE}/ai-suggest-reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ leadId: leadId || "", dealId: dealId || "" }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const fallback =
        res.status === 404
          ? "AI replies need the backend update deployed first."
          : `HTTP ${res.status}`;
      return { ok: false, error: data.error || fallback };
    }
    return { ok: true, draft: data.draft || "" };
  } catch (e) {
    return { ok: false, error: e?.message || "Network error" };
  }
}

// Verifies credentials and lists SMS-capable numbers on the team-line user.
// Returns { ok, smsNumbers } or { ok: false, error }.
export async function fetchRcStatus() {
  try {
    const res = await authedFetch(`${API_BASE}/rc-status`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const fallback =
        res.status === 404
          ? "Texting isn't live yet — the backend needs its update deployed."
          : `HTTP ${res.status}`;
      return { ok: false, error: data.error || fallback };
    }
    return { ok: true, smsNumbers: data.smsNumbers || [] };
  } catch (e) {
    return { ok: false, error: e?.message || "Network error" };
  }
}
